/* eslint-disable max-lines */
import { PrincipalTypeEnum } from '@ulysses-inc/harami_api_client'
import { ExhaustiveError } from '~/utils/error'
import type { Image } from '../image/image'
import type {
  AppRoleEnum,
  CompanyContractPlan,
  CompanyContractStatus,
  Improve as ApiImprove,
  ImproveStatus,
  IndicatedIssueStatus,
  NewFeatureVersionEnum,
  PlaceNodeTypeEnum,
  ProductMasterRecord,
  ReportPrincipal,
  ReportStatusEnum,
  ResponseSetExcelOutputSettingEnum,
  TemplateIconTypeEnum,
  TemplateLayoutTypeEnum,
  TemplateReportNamingRule,
  TemplateReportNamingRuleJoinPositionEnum,
} from '@ulysses-inc/harami_api_client'

// TODO: 必要なフィールドを整理する
// labels: ph.3

type Company = {
  id: number
  name: string
  phoneNumber?: string
  postalCode?: string
  address?: string
  contactMailAddress?: string
  userLimit: number
  startContractDate?: Date
  endContractDate?: Date
  contractStatus: CompanyContractStatus
  contractPlan: CompanyContractPlan
  wovnToken?: {
    token: string
  }
  wovnEnableUsers?: {
    user: User
  }[]
  users?: User[]
}

type Place = {
  id?: number
  name?: string
  placeNodeId?: number
}

type PlaceNode = {
  id: number
  uuid: string
  type: PlaceNodeTypeEnum
  isRootNode: number
  place?: Place
  placeGroup?: {
    id?: number
    name?: string
    placeNodeId?: number
  }
  parentNodes?: PlaceNode[]
  nodes?: PlaceNode[]
}

type User = {
  id: number
  uuid: string
  name: string
  nameKana: string
  email: string
  password: string
  role: {
    role?: AppRoleEnum
  }
  loginId: string
  userGroups?: {
    id: number
    name: string
    users?: User[]
  }[]
  placeNodes?: PlaceNode[]
  company: Company
  isCompleteTutorial: number
  announcedNewFeatureVersion: NewFeatureVersionEnum
  isEditExcelConversion: number
  createdAt?: string
}

type MultipleChoiceSet = {
  uuid?: string
  name?: string
  excelOutputOption?: ResponseSetExcelOutputSettingEnum
  responses?: {
    uuid?: string
    name?: string
    discriminationKey?: string
    categories?: {
      name?: string
    }[]
  }[]
}
type ApprovalFlow = {
  id?: number
  name?: string
  uuid?: string
  description?: string
  steps?: {
    approvers?: User[]
  }[]
}

type Icon = {
  iconType?: TemplateIconTypeEnum
}

export type HintInfo = {
  id?: string
  name: string
}

type TemplateData = {
  id?: number
  versionId?: number
  name: string
  multipleChoiceSets?: MultipleChoiceSet[]
  approvalFlows?: ApprovalFlow[]
  isShowScore?: boolean
  isEditable?: boolean
  isExcelConversion?: boolean
  isHideQuestionOptions?: boolean
  isAudit?: boolean
  isKeyboard?: boolean
  layoutType?: TemplateLayoutTypeEnum
  icons?: Icon[]
  // NOTE: TemplateData としてまとめて扱いたいプロパティを追加する
  hints: HintInfo[]
  rule?: TemplateReportNamingRule
}

type IndicatedIssueLabel = {
  id?: number
  uuid?: string
  name?: string
  color?: string
}

// 指摘の画像。既存の question 画像と同じ Image union（LocalImage | UploadedImage）
// を使う。提出時に useReUploadAllLocalImages が LocalImage を UploadedImage に
// 置き換え、buildReport は API 送信時に UploadedImage のみを抽出して送る。
// API レスポンスを受けるときは convertApiImproveToModel で boundary 変換する。
type IndicatedIssue = {
  id?: number
  uuid?: string
  images?: Image[]
  comment?: string
  indicatedIssueDate?: Date
  indicatedIssueGroupUUID?: string
  labels?: IndicatedIssueLabel[]
  isRequired?: number
  // 管理画面（harami_web）が指摘の状態表示・提出ボタンの活性判定で
  // status === Incomplete を比較するため、iOS 提出と揃えて作成時に付与する。
  // 本修正前に作成された指摘は undefined のことがあるため optional。API 送信時は
  // buildReport の convertModelImproveToApi が Incomplete を補完する（下書き保存・
  // 提出のどちらの経路も buildReport を通るため同じ補完が効く）。
  status?: IndicatedIssueStatus
}

type IndicatedIssueGroup = {
  id?: number
  uuid?: string
  indicatedIssues?: IndicatedIssue[]
}

type UserGroup = {
  id: number
  name: string
  users?: User[]
}

export type Improve = {
  id?: number
  uuid: string
  status?: ImproveStatus
  indicatedIssueGroups?: IndicatedIssueGroup[]
  auditUser?: User
  placeUserGroup?: UserGroup
}

// API レスポンス由来の Improve（画像は { uuid, url } 形式）をモデル側の
// Improve（画像は LocalImage | UploadedImage の union）へ変換する。
// editReport 開始時の boundary で使う。
const convertApiImproveImageToModel = (apiImage: {
  uuid?: string
  url?: string
  recordedAt?: Date
}): Image | null => {
  if (!apiImage.uuid || !apiImage.url) return null
  return {
    type: 'uploaded',
    key: apiImage.uuid,
    recordedAt: apiImage.recordedAt ?? new Date(0),
    file: { uuid: apiImage.uuid, url: apiImage.url },
  }
}

type ApiImproveLike =
  | {
      id?: number
      uuid?: string
      status?: ImproveStatus
      indicatedIssueGroups?: {
        id?: number
        uuid?: string
        indicatedIssues?: {
          id?: number
          uuid?: string
          images?: { uuid?: string; url?: string; recordedAt?: Date }[]
          comment?: string
          indicatedIssueDate?: Date
          indicatedIssueGroupUUID?: string
          labels?: IndicatedIssueLabel[]
          isRequired?: number
          status?: IndicatedIssueStatus
        }[]
      }[]
      auditUser?: User
      placeUserGroup?: UserGroup
    }
  | undefined

export const convertApiImproveToModel = (
  apiImprove: ApiImproveLike,
): Improve | undefined => {
  if (!apiImprove?.uuid) return undefined
  return {
    ...apiImprove,
    uuid: apiImprove.uuid,
    indicatedIssueGroups: apiImprove.indicatedIssueGroups?.map(group => ({
      ...group,
      indicatedIssues: group.indicatedIssues?.map(issue => ({
        ...issue,
        images: issue.images?.flatMap(img => {
          const converted = convertApiImproveImageToModel(img)
          return converted ? [converted] : []
        }),
      })),
    })),
  }
}

export type ReportQuestionsIndicatedIssueGroup = {
  reportUUID?: string
  reportNodeId?: number
  reportNodeUUID?: string
  indicatedIssueGroupUUID?: string
}

export type ReportData = {
  id?: number
  uuid: string
  name?: string
  originalName?: string
  // 命名ルールから分離されたひな形名/動的部分。翻訳の品質改善に使用する。
  templatePart?: string
  dynamicPart?: string
  reportNameDelimiter?: string
  reportNameJoinPosition?: TemplateReportNamingRuleJoinPositionEnum
  reportDate?: Date
  status?: {
    status?: ReportStatusEnum
  }
  schedules?: {
    id?: number
  }[]
  templateId?: number
  multipleChoiceSets?: MultipleChoiceSet[]
  approvals?: {
    uuid?: string
  }[]
  approvalFlows?: ApprovalFlow[]
  place?: {
    name?: string
    nodeId?: string
    reportId?: number
  }
  isShowScore?: boolean
  isEditable?: boolean
  isExcelConversion?: boolean
  isHideQuestionOptions?: boolean
  isAudit?: boolean
  isKeyboard?: boolean
  layoutType?: TemplateLayoutTypeEnum
  icons?: Icon[]
  // ReportData は API レスポンスの snapshot として扱うので、improve も API 型
  // (画像は { uuid, url } 形式) のまま保持する。store の state.improve はモデル型
  // (画像は LocalImage | UploadedImage の union) を使い、convertApiImproveToModel
  // で boundary 変換する。
  improve?: ApiImprove
  linkedIndicatedIssues?: ReportQuestionsIndicatedIssueGroup[]
  // 記録者 principal (GET /v2/reports/{reportId} の principal)。記録者が共用端末
  // (Device) かの判定に使う。principal 導入前の旧データ等では null / undefined。
  principal?: ReportPrincipal | null
  templateVersionId?: number
  hints?: HintInfo[]
  product?: {
    id: number
    name?: string
  }
}

type NewReportModeBase = {
  template: TemplateData
  reportUuid: string
  reportDate: Date
  placeNode: {
    uuid: string
    name: string
  }
  rule?: TemplateReportNamingRule
  originalName?: string
  // 記録者スナップショット。User 記録者がオフラインで新規作成したときに、作成時点の
  // User UUID + 名前を凍結して保持する。作成者と別ユーザーがアップロードしても記録者を
  // 作成者のまま保つために送信・表示で用いる。Device 記録者・本フィールド導入前に
  // 作成されたデータでは undefined となり、表示・送信は session（認証主体）へフォールバックする。
  reportAssignee?: {
    uuid: string
    name: string
  }
}

type ReportModeFromTemplate = NewReportModeBase & {
  modeType: 'newReportFromTemplate'
}

type ReportModeFromProductMaster = NewReportModeBase & {
  modeType: 'newReportFromProductMaster'
  product: ProductMasterRecord
}

type ReportModeFromSchedule = NewReportModeBase & {
  modeType: 'newReportFromSchedule'
  scheduleId: number
  scheduleName: string
}

// 👇 editReport は以下の場合すべて共通
// * レポート一覧からの編集
// * スケジュールカードからの編集
type ReportModeEditReport = {
  modeType: 'editReport'
  report: ReportData
  rule?: TemplateReportNamingRule
}

export type ReportMode =
  | ReportModeFromTemplate
  | ReportModeFromSchedule
  | ReportModeFromProductMaster
  | ReportModeEditReport

const getReportName = (reportMode: ReportMode): string => {
  switch (reportMode.modeType) {
    case 'editReport':
      return reportMode.report.name ?? ''
    case 'newReportFromTemplate':
      return reportMode.template.name
    case 'newReportFromSchedule':
      return reportMode.scheduleName
    case 'newReportFromProductMaster':
      return reportMode.template.name
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const isAudit = (reportMode: ReportMode): boolean => {
  switch (reportMode.modeType) {
    case 'editReport':
      return reportMode.report.isAudit ?? false
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.template.isAudit ?? false
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const isShowScore = (reportMode: ReportMode): boolean => {
  switch (reportMode.modeType) {
    case 'editReport':
      return reportMode.report.isShowScore ?? false
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.template.isShowScore ?? false
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const isEditable = (reportMode: ReportMode): boolean => {
  switch (reportMode.modeType) {
    case 'editReport':
      return reportMode.report.isEditable ?? false
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.template.isEditable ?? false
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const layoutType = (
  reportMode: ReportMode,
): TemplateLayoutTypeEnum | undefined => {
  switch (reportMode.modeType) {
    case 'editReport':
      return reportMode.report.layoutType
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.template.layoutType
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const getReportUuid = (reportMode: ReportMode): string => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.reportUuid
    case 'editReport':
      return reportMode.report.uuid
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const getReportId = (reportMode: ReportMode): number | undefined => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return undefined
    case 'editReport':
      return reportMode.report.id
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const getReportDate = (reportMode: ReportMode): Date | undefined => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.reportDate
    case 'editReport':
      return reportMode.report.reportDate
    default:
      throw new ExhaustiveError(reportMode)
  }
}

type PlaceNodeType = Extract<
  ReportMode,
  { modeType: 'newReportFromTemplate' }
>['placeNode']

const getPlaceNode = (reportMode: ReportMode): PlaceNodeType | undefined => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.placeNode
    case 'editReport':
      return reportMode.report.place?.nodeId
        ? {
            uuid: reportMode.report.place.nodeId,
            name: reportMode.report.place.name ?? '',
          }
        : undefined
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const getHintInfoList = (reportMode: ReportMode): HintInfo[] => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.template.hints
    case 'editReport':
      return reportMode.report.hints ?? []
    default:
      throw new ExhaustiveError(reportMode)
  }
}

const getRule = (
  reportMode: ReportMode,
): TemplateReportNamingRule | undefined => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'editReport':
      return reportMode.rule
    case 'newReportFromProductMaster':
      return reportMode.template.rule
    default:
      throw new ExhaustiveError(reportMode)
  }
}

/**
 * 記録者スナップショット（User の作成時 UUID + 名前）を取得する。
 * User がオフラインで新規作成したレポートのみ保持する。Device 記録者・移行前データ・
 * editReport（記録者はサーバ principal 由来）では undefined を返し、呼び出し側で
 * session（認証主体）へフォールバックする。
 */
const getRecorder = (
  reportMode: ReportMode,
): { uuid: string; name: string } | undefined => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return reportMode.reportAssignee
    case 'editReport':
      return undefined
    default:
      throw new ExhaustiveError(reportMode)
  }
}

/**
 * 記録者が共用端末 (Device principal) のレポートかを判定する。
 * 指摘 (カイゼン) の帰属は User 前提のため、Device が記録したレポートでは
 * 編集時も指摘の操作 UI を出さない、といった判定に使う。
 * principal を持たない旧データは User 記録として false に倒す (fail-open)。
 * 新規作成モードは操作中の認証主体自身が記録者になるため false
 * (共用端末での新規記録は appMode.isKiosk 側で制御する)。
 */
const isRecordedByDevice = (reportMode: ReportMode): boolean => {
  switch (reportMode.modeType) {
    case 'editReport':
      return reportMode.report.principal?.type === PrincipalTypeEnum.Device
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
    case 'newReportFromProductMaster':
      return false
    default:
      throw new ExhaustiveError(reportMode)
  }
}

/** レポート名が付与される前の元のレポート名を取得する。 */
const getOriginalNameToInherit = (
  reportMode: ReportMode,
): string | undefined => {
  switch (reportMode.modeType) {
    case 'newReportFromTemplate':
    case 'newReportFromSchedule':
      // 新規作成はoriginalNameから取得する
      return reportMode.originalName
    case 'newReportFromProductMaster':
      // 製品マスタからの新規作成時は originalName を持たないため、template.name を使用する
      return reportMode.template.name
    case 'editReport':
      // Put時はreport.originalNameから取得する
      return reportMode.report.originalName
    default:
      throw new ExhaustiveError(reportMode)
  }
}

export const ReportMode = {
  getReportName,
  getReportUuid,
  getReportId,
  getReportDate,
  getPlaceNode,
  getHintInfoList,
  getRule,
  getRecorder,
  getOriginalNameToInherit,
  isRecordedByDevice,
  layoutType,
  isAudit,
  isEditable,
  isShowScore,
}
